<template>
  <fragment>
    <v-form
      ref="digitalCaseRequestForm"
      @submit.prevent="submitForm"
    >
      <naj-box
        :title="local.caseDigitizationRequest.caseDetails"
        has-footer
      >
        <v-row>
          <v-col cols="4">
            <naj-input
              :type-id="formFieldType.number"
              :label="local.caseDigitizationRequest.caseCode"
              :value.sync="formModel.caseCode"
              validates="required"
            ></naj-input>
          </v-col>
          <v-col cols="4">
            <naj-input
              :type-id="formFieldType.selectbox"
              :label="local.caseDigitizationRequest.courtName"
              :value.sync="formModel.originalCourtId"
              :items="mergedActiveCourts"
              item-text="name"
              item-value="id"
              validates="required"
              @change="mapCurrentCourt"
            ></naj-input>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="4">
            <naj-input
              :type-id="formFieldType.number"
              :label="local.caseReply.filters.judgmentCode"
              :value.sync="formModel.judgementCode"
              validates="required|long"
            ></naj-input>
          </v-col>
          <v-col
            class="text-center"
            cols="4"
          >
            <v-row>
              <v-col cols="4">
                <label class="request-reason-label">{{ local.caseDigitizationRequest.addAttachment }}</label>
                <div
                  v-if="formModel.attachment.sharePointId && formModel.attachment.fileName"
                  class="d-flex justify-content-center align-items-center file-name h-75"
                >
                  {{ formModel.attachment.fileName }}
                </div>
                <div
                  v-if="(!formModel.attachment.sharePointId || !formModel.attachment.fileName)"
                  class="d-flex justify-content-center h-75 file-upload-icon"
                  @click="openRequestReasonAttachmentModal()"
                >
                  <v-icon>
                    mdi-cloud-upload
                  </v-icon>
                </div>
                <div
                  v-if="requiredAttachment"
                  class="v-messages theme--light error--text"
                  role="alert"
                >
                  <div class="v-messages__wrapper">
                    <div class="v-messages__message attachment-message">
                      {{ local.caseDigitizationRequest.requiredAttachment }}
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col cols="4">
                <v-btn
                  v-if="formModel.attachment.sharePointId && formModel.attachment.fileName"
                  icon
                  @click="handleViewAttachment(formModel.attachment)"
                >
                  <v-icon>
                    mdi-eye
                  </v-icon>
                </v-btn>
                <v-btn
                  v-if="formModel.attachment.sharePointId && formModel.attachment.fileName"
                  icon
                  @click="showDeleteReasonConfirmationModal = true; "
                >
                  <v-icon color="red">
                    mdi-delete
                  </v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <request-reason-modal
          :modal-visibility="showRequestReasonAttachmentModal"
          @addRequestReasonAttachment="addRequestReasonAttachment"
          @onClose="hideRequestReasonAttachmentModal"
        >
        </request-reason-modal>

        <render-attachment-modal
          :attachment-file="selectedFile"
          :modal-visibility="showViewAttachmentModal"
          @modalClose="hideViewAttachmentModal"
        >
        </render-attachment-modal>

        <naj-confirmation
          :show-modal="showDeleteReasonConfirmationModal"
          :title="local.caseDigitizationRequest.confirm"
          :message="local.caseDigitizationRequest.confirmMessage"
          :ok-btn-label="local.caseDigitizationRequest.yes"
          :close-btn-label="local.caseDigitizationRequest.no"
          @ok="() => {
            showDeleteReasonConfirmationModal = false;
            handleRemoveReason();
          }"
          @cancel="showDeleteReasonConfirmationModal = false"
        ></naj-confirmation>

        <template v-slot:footer>
          <span />
          <naj-button
            type="submit"
            color="primary"
            :loading="loading"
          >
            {{ local.common.saveAndSend }}
          </naj-button>
          <naj-confirmation
            :show-modal="showSendRequestConfirmationModal"
            :title="local.common.confirmationSendRequest"
            :message="local.caseRelatedRequest.sendRequestConfirmationMessage"
            :ok-btn-label="local.common.yes"
            :close-btn-label="local.common.no"
            @ok="onSubmit"
            @cancel="showSendRequestConfirmationModal = false"
          ></naj-confirmation>
          <naj-confirmation
            :show-modal="showInformationModal"
            :max-width="750"
            :is-persistent="true"
            :title="local.caseRelatedRequest.successAlertMessage"
            :close-btn-label="local.common.close"
            @cancel="closeInformationModal"
          >
            <template slot="message-content">
              <v-row>
                <v-col>
                  <naj-alert type="success">
                    <template slot="content">
                      {{ local.caseRelatedRequest.requestSendSuccessfully }}، {{ local.caseRelatedRequest.requestCode }} {{ requestCodeResult }}
                    </template>
                  </naj-alert>
                </v-col>
              </v-row>
            </template>
          </naj-confirmation>
        </template>
      </naj-box>
    </v-form>
  </fragment>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import RequestReasonModal from '@/components/shared/request-reason-modal/index';
const { mapActions } = createNamespacedHelpers('LawsuitModule');
const { mapState: mapLUTState, mapActions: mapLUTActions } = createNamespacedHelpers(
  'LookupsModule'
);
import RenderAttachmentModal from '@/components/shared/attachment-modal/render-attachment-modal';

export default ({
  components: {
    RequestReasonModal,
    RenderAttachmentModal,
  },
  data() {
    return {
      requestCodeResult: null,
      showInformationModal: false,
      showSendRequestConfirmationModal: false,
      showRequestReasonAttachmentModal: false,
      loading: false,
      formModel: { attachment: {} },
      showViewAttachmentModal: false,
      selectedFile: {},
      showDeleteReasonConfirmationModal: false,
      requiredAttachment: false,
    };
  },
  computed: { ...mapLUTState(['mergedActiveCourts']) },
  watch: {
    'formModel.attachment'() {
      if (Object.keys(this.formModel.attachment).length)
        this.requiredAttachment = false;
    },
  },
  mounted() { this.getMergedActiveCourts(); },
  methods: {
    ...mapLUTActions(['getMergedActiveCourts']),
    ...mapActions(['addCasesDigitizationRequest']),
    openRequestReasonAttachmentModal() {
      this.showRequestReasonAttachmentModal = true;
    },
    closeInformationModal() {
      this.showInformationModal = false;
      this.$router.push({
        name: 'requests',
        params: { targetTab: '#tab-3' },
      });
    },
    addRequestReasonAttachment(file) {
      this.$set(this.formModel.attachment, 'fileCategory', 1679);
      this.$set(this.formModel.attachment, 'sharePointId', file.sharePointId);
      this.$set(this.formModel.attachment, 'fileName', file.sharePointFileName);
      this.$set(this.formModel.attachment, 'extension', file.extension);
    },
    hideRequestReasonAttachmentModal() {
      this.showRequestReasonAttachmentModal = false;
    },
    submitForm() {
      this.$refs.digitalCaseRequestForm.$validator.validateAll()
        .then((isValid) => {
          if (isValid && Object.keys(this.formModel.attachment).length === 0) {
            this.requiredAttachment = true;
            return;
          }
          if (isValid)
            this.showSendRequestConfirmationModal = true;
          else
            this.requiredAttachment = true;
        });
    },
    isImageFile(extension) {
      const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp'];
      const fileExtension = extension.toLowerCase().substring(extension.lastIndexOf('.'));
      return imageExtensions.includes(fileExtension);
    },
    onSubmit() {
      this.loading = true;
      this.showInformationModal = false;
      this.requestCodeResult = null;
      this.addCasesDigitizationRequest(this.formModel).then((res) => {
        this.loading = false;
        this.$emit('successSubmit');
        this.showSendRequestConfirmationModal = false;
        this.$refs.digitalCaseRequestForm.$validator.reset();
        this.requestCodeResult = res.data;
        this.showInformationModal = true;
      }).catch(() => {
        this.loading = false;
      });
    },
    mapCurrentCourt() {
      const mergedCourtId = ((this.mergedActiveCourts || []).find(c => c.id === this.formModel.originalCourtId) || {}).mergedCourtId;
      this.$set(this.formModel, 'currentCourtId', mergedCourtId || null);
    },
    handleViewAttachment(file) {
      this.$set(this, 'selectedFile', {
        name: file.fileName || file.sharePointFileName,
        isImage: this.isImageFile(file.extension),
        sharePointId: file.sharePointId,
      });
      this.showViewAttachmentModal = true;
    },
    hideViewAttachmentModal() {
      this.showViewAttachmentModal = false;
    },
    handleRemoveReason() {
      this.$set(this.formModel, 'attachment', {});
      this.requiredAttachment = true;
    },
  },
});
</script>
<style>
.attachment-message {
  text-align: center;
}
</style>